import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { currencySymbol } from "@/lib/currency";
import type { PurchaseOrderHistory } from "@/services/coinPurchaseService";

interface Props {
  order: PurchaseOrderHistory | null;
  onClose: () => void;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <span className="text-xs font-mono text-slate-700 flex-1 break-all">{value}</span>
        <button
          onClick={copy}
          className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
          title="Copy to clipboard"
        >
          {copied
            ? <Check className="w-3.5 h-3.5 text-emerald-500" />
            : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  DISPUTED: "bg-amber-50 text-amber-700 border-amber-200",
  REFUNDED: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABELS: Record<string, string> = {
  PAID: "Paid",
  DISPUTED: "Disputed",
  REFUNDED: "Refunded",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function PurchaseReceiptModal({ order, onClose }: Props) {
  if (!order) return null;

  const sym = currencySymbol(order.currency as "INR" | "USD");
  const amount = order.amount / 100;
  const fmtAmount = `${sym}${Number.isInteger(amount) ? amount.toLocaleString() : amount.toFixed(2)}`;

  return (
    <Dialog open={!!order} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-bold text-slate-800">Payment Receipt</DialogTitle>
        </DialogHeader>

        {/* Summary bar */}
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-lg font-black text-slate-800 leading-none">{order.coins.toLocaleString()} BMC Coins</p>
            <p className="text-xs text-slate-400 mt-0.5">{fmtAmount} · {order.currency}</p>
          </div>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${STATUS_STYLES[order.status] ?? STATUS_STYLES.PAID}`}>
            {STATUS_LABELS[order.status] ?? order.status}
          </span>
        </div>

        {/* IDs for dispute / refund reference */}
        <div className="flex flex-col gap-3 mt-1">
          {order.razorpayPaymentId && (
            <CopyField label="Payment ID (Razorpay)" value={order.razorpayPaymentId} />
          )}
          <CopyField label="Order ID (Razorpay)" value={order.razorpayOrderId} />
          <CopyField label="BMC Order Reference" value={order.orderId} />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Initiated</p>
            <p className="text-xs text-slate-600">{fmtDate(order.createdAt)}</p>
          </div>
          {order.paidAt && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Paid at</p>
              <p className="text-xs text-slate-600">{fmtDate(order.paidAt)}</p>
            </div>
          )}
        </div>

        {/* Dispute / refund help note */}
        {order.status === "DISPUTED" || order.status === "REFUNDED" ? (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-1">
            {order.status === "DISPUTED"
              ? "A dispute is open on this payment. Share the Payment ID with support if needed."
              : "This purchase has been refunded. Coins were removed from your balance."}
          </p>
        ) : (
          <p className="text-xs text-slate-400 mt-1">
            To raise a refund or dispute, share the <strong>Payment ID</strong> with our support team at{" "}
            <a href="mailto:support@bookmycoaching.com" className="text-indigo-600 hover:underline">
              support@bookmycoaching.com
            </a>
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
